import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useJobStream, type LogLine } from '@/api/jobs';
import { organizationQuery, qk } from '@/api/queries';
import type { Organization, Run, RunSource } from '@/api/types';
import { Button, Card, Icon, IconTile, Notch, Pill, useToast, type IconName, type Tone } from '@/design-system';
import { cx } from '@/lib/cx';
import { formatNumber } from '@/lib/format';
import { errorText } from '@/shell/errorText';
import { LoadError } from '@/shell/LoadError';
import { cancelRun, researchRunsQuery, snapshotsQuery, startRun } from './api';
import { ItemsTable } from './ItemsTable';
import s from './research.module.css';

type Tv = (k: string, o?: Record<string, unknown>) => string;
type Report = {
  brand?: { description?: string | null; colors?: string[] };
  contacts?: { type: string; value: string }[];
  notes?: { code: string; params?: Record<string, unknown> }[];
};

const RUN_TONE: Record<string, Tone> = { queued: 'acc', running: 'acc', partial: 'warn', completed: 'ok', failed: 'err', cancelled: 'mute' };
const SOURCE_TONE: Record<string, Tone> = { ok: 'ok', partial: 'warn', failed: 'err', skipped: 'mute', pending: 'mute', running: 'acc' };
const SOURCE_ICON: Record<string, IconName> = { website: 'globe', instagram: 'instagram', telegram: 'send', file: 'file-spreadsheet' };
const STEP_KEYS = ['collect', 'extract', 'normalize', 'review'] as const;
const ACTIVE = new Set(['queued', 'running']);

/** «14:02:11» in Tashkent. */
function clock(at: string): string {
  return new Intl.DateTimeFormat('en-GB', { timeZone: 'Asia/Tashkent', hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23' }).format(new Date(at));
}

function displayUrl(url: string | null | undefined): string {
  return (url ?? '').replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/$/, '');
}

/** A note's sentence: one RU/UZ template per code (P2-A05 `_notes`), with the design's wording. */
export function noteText(n: { code: string; params?: Record<string, unknown> }, t: Tv): string {
  const p = n.params ?? {};
  const kind = (k: unknown) => t(`kind.${String(k)}`, { defaultValue: String(k) });
  switch (n.code) {
    case 'prices_unverified':
      return Array.isArray(p.sources) && p.sources.length
        ? t('notes.prices_unverified_sources', { sources: p.sources.map(kind).join(t('notes.and')) })
        : t('notes.prices_unverified', { count: Number(p.count ?? 0) });
    case 'image_rights_to_confirm':
      return p.source ? t('notes.image_rights_to_confirm_source', { source: kind(p.source) }) : t('notes.image_rights_to_confirm', { count: Number(p.count ?? 0) });
    case 'source_failed':
      return t('notes.source_failed', { kind: kind(p.kind), reason: t(`reason.${String(p.reason)}`, { defaultValue: String(p.reason ?? '') }) });
    case 'few_products':
      return t('notes.few_products', { count: Number(p.count ?? 0) });
    case 'design_hint':
      return String(p.text ?? '');
    default:
      return t(`notes.${n.code}`, { ...p, defaultValue: n.code });
  }
}

/** The research block (DESIGN_SPEC §4.3 Research Data; P2-D01–D03): the Org tab and the site's Research tab. */
export function ResearchBlock({ orgId, siteId }: { orgId: string; siteId?: string }) {
  const { t } = useTranslation('research');
  const tv = t as unknown as Tv;
  const { t: tc } = useTranslation('common');
  const qc = useQueryClient();
  const toast = useToast();
  const runs = useQuery(researchRunsQuery(orgId));
  const org = useQuery(organizationQuery(orgId)).data;
  const snapshots = useQuery(snapshotsQuery(orgId)).data;
  const run = runs.data?.[0] ?? null;
  const active = !!run && ACTIVE.has(run.status);
  // The live job: its snapshot gives the progress and the safe log, then the stream keeps them current.
  const stream = useJobStream(run?.job_id);

  // While the job runs, the run row (steps, counts) is refreshed on its events, at most every 2 s.
  const lastRefetch = useRef(0);
  const progress = stream.job?.progress;
  const finished = stream.finished;
  useEffect(() => {
    if ((!active && !finished) || (progress === undefined && !finished)) return;
    const now = Date.now();
    if (!finished && now - lastRefetch.current < 2000) return;
    lastRefetch.current = now;
    void qc.invalidateQueries({ queryKey: qk.researchRuns(orgId) });
    if (finished) void qc.invalidateQueries({ queryKey: ['research', 'items', orgId] });
  }, [progress, finished, active, qc, orgId]);

  const restart = useMutation({
    mutationFn: () => startRun(orgId),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: qk.researchRuns(orgId) });
      toast(run ? t('hero.restarted', { number: r.number }) : t('empty.started', { number: r.number }));
    },
    onError: (err) => toast(errorText(err, tc)),
  });
  const cancel = useMutation({
    mutationFn: () => cancelRun(run?.id ?? ''),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: qk.researchRuns(orgId) });
      toast(t('hero.cancelled', { number: r.number }));
    },
    onError: (err) => toast(errorText(err, tc)),
  });

  if (runs.isError) return <LoadError error={runs.error} onRetry={() => void runs.refetch()} />;
  if (!runs.data) return null;
  if (!run) {
    return (
      <Card className={s.emptyCard}>
        <span className={s.emptyTitle}>{t('empty.title')}</span>
        <span className={s.emptyText}>{org && !org.sources.length ? t('empty.no_sources') : t('empty.text')}</span>
        <Button icon="scan-search" className={s.alignStart} disabled={restart.isPending || (!!org && !org.sources.length)} onClick={() => restart.mutate()}>
          {t('empty.start')}
        </Button>
      </Card>
    );
  }

  // The summary cards read the newest report: this run's, or the last finished run's while this one runs.
  const reportRun = runs.data.find((r) => r.report);
  const report = (reportRun?.report ?? (snapshots?.[0]?.report as Report | undefined) ?? null) as Report | null;
  const pct = Math.round((active && stream.job ? stream.job.progress : run.progress) * 100);
  const eta = active ? (stream.job?.eta_seconds ?? run.eta_seconds) : null;
  const etaText = !active ? t('hero.eta_done') : eta ? t('hero.eta_minutes', { count: Math.max(1, Math.round(eta / 60)) }) : t('hero.eta_unknown');
  const failed = run.sources.filter((x) => x.status === 'failed' || x.status === 'partial');
  const log: LogLine[] = stream.log.slice(-9);
  const counts = run.counts as Record<string, number | undefined>;
  const firstError = failed[0];
  const steps = STEP_KEYS.map((key) => run.steps.find((x) => x.key === key) ?? { key, state: 'pending', progress: 0 });
  const status = run.status;

  return (
    <>
      <div className={s.top}>
        <Card padding={24} relative className={s.hero}>
          <Notch actions={[{ icon: 'rotate-ccw', label: t('hero.restart'), onClick: () => restart.mutate() }]} />
          <div className={s.heroHead}>
            <div className={s.heroEyebrow}>
              <span className={s.eyebrow12}>{t('hero.run', { number: run.number })}</span>
              <Pill tone={RUN_TONE[status] ?? 'mute'} size="sm">
                {tv(`hero.status.${status}`, { defaultValue: status })}
              </Pill>
              {/* Stopping a run is additive (P2-D01). */}
              {active && (
                <button type="button" className={s.textBtn} onClick={() => cancel.mutate()} disabled={cancel.isPending}>
                  {t('hero.cancel')}
                </button>
              )}
            </div>
            <span className={s.claim}>{t('hero.claim')}</span>
          </div>
          <div className={s.heroNumbers}>
            <div className={s.pctRow}>
              <span className={s.pct}>{pct}</span>
              <span className={s.pctSign}>%</span>
            </div>
            <div className={s.heroMeta}>
              <span>
                {t('hero.eta')}
                <span className={s.etaValue}>{etaText}</span>
              </span>
              <span>{t('hero.processed', { count: run.sources.length, failed: failed.length })}</span>
            </div>
          </div>
          <div className={s.steps}>
            {steps.map((st, i) => {
              const [name, desc] = t(`steps.${st.key as 'collect'}`, { returnObjects: true }) as unknown as [string, string];
              const state = String(st.state);
              return (
                <div key={String(st.key)} className={cx(s.step, state === 'running' && s.stepRunning)}>
                  <div className={s.stepHead}>
                    <span className={s.stepNo}>{String(i + 1).padStart(2, '0')}</span>
                    <Icon
                      name={state === 'done' ? 'circle-check' : state === 'running' ? 'loader' : 'circle-dashed'}
                      size={14}
                      className={state === 'done' ? s.iconDone : state === 'running' ? s.iconRun : s.iconWait}
                    />
                  </div>
                  <div className={s.stepText}>
                    <span className={s.stepName}>{name}</span>
                    <span className={s.stepDesc}>{desc}</span>
                  </div>
                  <div className={s.stepTrack}>
                    <div className={s.stepFill} style={{ width: `${Math.round(Number(st.progress) * 100)}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
        <div className={s.log}>
          <div className={s.logHead}>
            <span className={s.logTitle}>{t('log.title')}</span>
            {(stream.live || active) && (
              <span className={s.live}>
                <span className={s.liveDot} />
                {t('log.live')}
              </span>
            )}
          </div>
          <div className={s.logLines} role="log" aria-label={t('log.title')}>
            {log.length === 0 && <span className={s.logEmpty}>{t('log.empty')}</span>}
            {log.map((l, i) => (
              <div key={`${l.at}-${i}`} className={s.logLine}>
                <span className={s.logTime}>{clock(l.at)}</span>
                <span className={l.level === 'warning' || l.level === 'error' ? s.logWarn : l.level === 'notice' ? s.logNote : s.logInfo}>{l.message}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className={s.counters}>
        <Counter label={t('counts.products')} value={counts.products} sub={t('counts.products_sub', { count: counts.duplicates_merged ?? 0 })} />
        <Counter label={t('counts.categories')} value={counts.categories} sub={t('counts.categories_sub')} />
        <Counter label={t('counts.images')} value={counts.images} sub={counts.images ? t('counts.images_sub') : t('counts.images_none')} tone={counts.images ? 'warn' : undefined} />
        <Counter
          label={t('counts.errors')}
          value={counts.source_errors ?? failed.length}
          sub={firstError ? t('counts.error_sub', { kind: tv(`kind.${firstError.kind}`, { defaultValue: firstError.kind }), reason: tv(`reason.${firstError.reason_code}`, { defaultValue: firstError.reason_code ?? firstError.status }) }) : t('counts.errors_none')}
          tone={firstError ? 'err' : undefined}
        />
      </div>
      <div className={s.middle}>
        <Sources run={run} org={org} />
        <Card className={s.brand}>
          <span className={s.cardTitle}>{t('brand.title')}</span>
          {report?.brand?.colors?.length ? (
            <div className={s.swatches}>
              {report.brand.colors.slice(0, 4).map((c) => (
                <div key={c} className={s.swatch} style={{ background: c }} title={c.toUpperCase()} />
              ))}
            </div>
          ) : null}
          {report?.brand?.description ? (
            <p className={s.desc}>{report.brand.description}</p>
          ) : (
            !report?.brand?.colors?.length && !report?.contacts?.length && <span className={s.muted}>{t('brand.empty')}</span>
          )}
          {!!report?.contacts?.length && (
            <div className={s.chips}>
              {report.contacts
                .filter((c) => c.type === 'phone' || c.type === 'address' || c.type === 'email')
                .map((c) => (
                  <span key={`${c.type}-${c.value}`} className={s.chip}>
                    {c.value}
                  </span>
                ))}
            </div>
          )}
        </Card>
        <Card className={s.notes}>
          <span className={s.cardTitle}>{t('notes.title')}</span>
          {!report?.notes?.length && <span className={s.muted}>{t('notes.empty')}</span>}
          {(report?.notes ?? []).map((n, i) => (
            <div key={`${n.code}-${i}`} className={s.note}>
              <span className={s.noteNo}>{String(i + 1).padStart(2, '0')}</span>
              <span>{noteText(n, tv)}</span>
            </div>
          ))}
        </Card>
      </div>
      <ItemsTable orgId={orgId} siteId={siteId} nextVersion={(snapshots?.[0]?.version ?? 0) + 1} />
    </>
  );
}

function Counter({ label, value, sub, tone }: { label: string; value: number | undefined; sub: string; tone?: 'warn' | 'err' }) {
  const { t: tc } = useTranslation('common');
  return (
    <Card padding={20} className={s.counter}>
      <span className={s.counterLabel}>{label}</span>
      <div className={s.counterRow}>
        <span className={s.counterValue}>{value === undefined ? tc('dash') : formatNumber(value)}</span>
        <span className={cx(s.counterSub, tone === 'warn' && s.subWarn, tone === 'err' && s.subErr)}>{sub}</span>
      </div>
    </Card>
  );
}

/** «Источники»: what this run read, with its status (the file source shows the uploaded file's name). */
function Sources({ run, org }: { run: Run; org: Organization | undefined }) {
  const { t } = useTranslation('research');
  const tv = t as unknown as Tv;
  const fileLabel = org?.sources.find((x) => x.kind === 'file')?.label;
  const name = (x: RunSource) => (x.kind === 'file' ? (x.url ?? fileLabel ?? t('kind.file')) : displayUrl(x.url));
  return (
    <Card padding={8}>
      <div className={s.sourcesTitle}>{t('sources.title')}</div>
      {run.sources.length === 0 && <div className={s.sourcesEmpty}>{t('sources.empty')}</div>}
      {run.sources.map((x) => (
        <div key={x.id} className={s.source}>
          <IconTile icon={SOURCE_ICON[x.kind] ?? 'globe'} size={34} iconSize={16} className={s.sourceTile} />
          <div className={s.sourceText}>
            <span className={s.sourceUrl}>{name(x)}</span>
            <span className={s.sourceMeta}>{tv(`sources.meta.${x.kind}`, { pages: formatNumber(x.pages), found: formatNumber(x.items_found), defaultValue: x.kind })}</span>
          </div>
          <Pill tone={SOURCE_TONE[x.status] ?? 'mute'} size="tagTight" className={s.sourcePill}>
            {tv(`sources.status.${x.status}`, { defaultValue: x.status })}
          </Pill>
        </div>
      ))}
    </Card>
  );
}
